/**
 * Hebrew display names for the lab objects.
 *
 * The object list, the record tabs and the related-list headers all render
 * `objectConfig[name].heLabel || <raw name>`. No lab object had an entry, so
 * every surface fell back to the table name and the CRM showed English —
 * «Lab repair», «Lab repair part», «Lab מלאי» — beside fully translated
 * objects like «חשבונות» and «מקרים».
 *
 * WHY A MODULE AND NOT ELEVEN LINES IN objectConfig
 *
 * The names arrive in more than one shape. `/api/objects` returns the table
 * name capitalised (`Lab_repair`), the related-list resolver can produce
 * `LabRepair`, and some callers lowercase. A lookup keyed on one spelling
 * silently misses the others and the label falls back to English again — which
 * is exactly the failure being fixed. So the key is normalised, and that
 * normalisation is testable on its own.
 */

/** Canonical form: letters only, lowercased. `Lab_repair`, `LabRepair` and
 *  `lab repair` all collapse to the same key. */
export function normalizeObjectName(name) {
  return String(name || '').toLowerCase().replace(/[^a-z]/g, '');
}

/**
 * Table name → Hebrew label.
 *
 * Keyed by the normalised form. The comment beside each is the table it
 * belongs to, so a reader can check the mapping against the schema without
 * guessing from the Hebrew.
 */
export const LAB_OBJECT_LABELS = {
  labrepair: 'תיקון מעבדה',                       // lab_repair
  labrepairhistory: 'היסטוריית תיקון מעבדה',      // lab_repair_history
  labrepairpart: 'חלקי חילוף',                    // lab_repair_part
  labchecklistitem: 'פריט צ׳קליסט',               // lab_checklist_item
  labchecklisttemplate: 'תבנית צ׳קליסט',          // lab_checklist_template
  labcolorcode: 'קוד צבע',                        // lab_color_code
  labstock: 'מלאי מעבדה',                         // lab_stock
  labassignment: 'הקצאת מכשיר למעבדה',            // lab_assignment
  labserialsequence: 'רצף מספרים סידוריים',       // lab_serial_sequence
  labserialissued: 'מספרים סידוריים שהונפקו',     // lab_serial_issued
  labrepairstatuspremigration: 'סטטוס תיקון לפני מיגרציה', // lab_repair_status_premigration
};

/** The icon every lab object shares, so they read as one module in a list. */
export const LAB_OBJECT_ICON = '🔬';
export const LAB_OBJECT_COLOR = '#0d7377';

/** The Hebrew label, or null when the object is not a lab object. Null rather
 *  than the raw name: the caller already has its own fallback chain, and
 *  returning a name here would shadow a better one it might have. */
export function labObjectLabel(name) {
  return LAB_OBJECT_LABELS[normalizeObjectName(name)] || null;
}

/** Ready-made objectConfig entries, so the map is built from one source. */
export const LAB_OBJECT_CONFIG = Object.keys(LAB_OBJECT_LABELS).reduce(
  (acc, key) => {
    acc[key] = {
      icon: LAB_OBJECT_ICON,
      color: LAB_OBJECT_COLOR,
      heLabel: LAB_OBJECT_LABELS[key],
    };
    return acc;
  }, {});

/**
 * Every key the Asset's lab-repair related list may be looked up under.
 *
 * The panel reads `relatedRecords[list.objectName]`, and that name arrives
 * from two places that disagree: the hardcoded Asset related-list map says
 * `LabRepair`, while a list derived from `/api/objects` says `Lab_repair` —
 * the table name, which is what the object registry reports.
 *
 * Only `LabRepair` was ever written, so an Asset whose panel came from the
 * registry showed «תיקון מעבדה (0)» directly above that repair's own PDF.
 * The records were fetched, parsed and thrown away for want of a spelling.
 */
export const LAB_REPAIR_RECORD_KEYS = ['LabRepair', 'Lab_repair', 'lab_repair'];
